import { Box } from '@mui/system';
import React, { useContext, useEffect, useState } from 'react';
import MediumTypography from '../../../components/formlib/MediumTypography';
import {
  AlertColor,
  Grid,
  Paper,
  Table,
  TableBody,
  TableContainer,
  Tooltip,
} from '@mui/material';
import DropdownComponent from '../../../components/formlib/DropdownComponent';
import DatePickerComponent from '../../../components/formlib/DatePickerComponent';
import CommentTextbox from '../../../components/formlib/CommentTextbox';
import InformationModal from '../../ifsp/InformationModal';
import dayjs, { Dayjs } from 'dayjs';
import { HeadCell, TableHeader } from '../../../components/formlib/TableHeader';
import ButtonComponent from '../../../components/formlib/ButtonComponent';
import { ReactComponent as InfoIcon } from '../../../assets/images/infoIcon.svg';
import { ReactComponent as Delete } from '../../../assets/images/deleteAlertIcon.svg';
import { ReactComponent as AddIcon } from '../../../assets/images/CircleAdd.svg';
import { ReactComponent as DownArrow } from '../../../assets/images/downarrowcolored.svg';
import { ReactComponent as UpArrow } from '../../../assets/images/uparrowcolored.svg';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  OutcomeProgress,
  updateIfspOutcomes,
  updateOriginalOutcomes,
} from '../../../redux/features/ifsp/ifspOutcomesSlice';
import OutcomeProgressReviewInfoTableRow from './OutcomeProgressReviewInfoTableRow';
import ModalPopup from '../../../components/formlib/ModalPopup';
import {
  deleteIfspOutcome,
  deleteOutcomeProgressInfo,
} from '../../../services/ifspClient/IfspApi';
import SnackBarComponent from '../../../components/formlib/SnackBarComponent';
import { LoaderContext, LoaderContextType } from '../../../layouts/AppSidebar';
import { useOutcomeValidationCheck } from './ifspOutcomeValidtionCheck';
import { datePickerMinDateMaxDateValidate } from '../../../utils/dateUtil';
import { v4 as uuidv4 } from 'uuid';
import { updateIfspUnsavedFields } from '../../../redux/features/ifsp/ifspUnsavedFieldSlice';

const info =
  'This page outlines the specific measurable results and outcomes that have been developed with the family, service coordinator and other members of the IFSP Team. The outcomes are based on the concerns identified through the evaluation and assessment process along with family priorities.';
const reviewInfo =
  'The degree to which progress toward achieving the results or outcomes identified is being made and whether modifications or revisions are necessary.';

type OutcomeProgressTableCellType = {
  date: string;
  progressDescription: string;
  progressRating: string;
  actions: string;
};

const outComesProgressInfoheadCells: HeadCell<OutcomeProgressTableCellType>[] =
  [
    {
      id: 'date',
      labelId: 'Date',
      defaultLabelId: 'Date',
      numeric: false,
      requiredSorting: false,
    },
    {
      id: 'progressDescription',
      labelId: 'Progress Description',
      defaultLabelId: 'Progress Description',
      numeric: false,
      requiredSorting: false,
    },
    {
      id: 'progressRating',
      labelId: 'Progress Rating',
      defaultLabelId: 'Progress Rating',
      numeric: false,
      requiredSorting: false,
    },
    {
      id: 'actions',
      labelId: 'Actions',
      defaultLabelId: 'Actions',
      numeric: false,
      requiredSorting: false,
    },
  ];

export const progressRatingsList = [
  {
    id: 'progress',
    label: "We're Making Progress",
  },
  {
    id: 'adjust',
    label: "Let's Make Adjustments",
  },
  {
    id: 'noLonger',
    label: 'No Longer a priority at this time',
  },
  {
    id: 'done',
    label: 'Outcome Met - We Did It',
  },
];

type ProgressInfoType = OutcomeProgress & { isSaveClicked: boolean };

type ChildAndFamilyOutcomesType = {
  isSaveClicked: boolean;
  isMainOutComeSaveSuccessTrigger: boolean;
  isUnsavedPopupConfirmed: boolean;
};

const ChildAndFamilyOutcomes = ({
  isSaveClicked,
  isMainOutComeSaveSuccessTrigger,
  isUnsavedPopupConfirmed,
}: ChildAndFamilyOutcomesType) => {
  const ifspOutComes = useAppSelector((state) => state.ifspOutComes);
  const ifspMainPageDetails = useAppSelector(
    (state) => state.ifspClientDetails.ifspClientDetails,
  );
  const ifspPermission = useAppSelector((state) => state.ifspPermissionDetails);
  const ifspUnsavedFields = useAppSelector((state) => state.ifspUnsavedFields);
  const dispatch = useAppDispatch();
  const [progressInfo, setProgressInfo] = useState<ProgressInfoType[]>([
    {
      id: '',
      tempId: uuidv4(),
      date: '',
      progressDescription: '',
      progressRating: '',
      isSaveClicked: false,
    },
  ]);
  const [openInfoModal, setOpenInfoModal] = useState<boolean>(false);
  const [openReviewInfoModal, setOpenReviewInfoModal] =
    useState<boolean>(false);
  const [showSubSection, setShowSubSection] = useState<boolean>(false);
  const [openIndex, setOpenIndex] = useState<number>(0);
  const [deleteOutComeIdAndIndex, setDeleteOutComeIdAndIndex] = useState<{
    id: string;
    index: number;
  }>({
    id: '',
    index: 0,
  });
  const [deleteOutcomeWarning, setDeleteOutcomeWarning] =
    useState<boolean>(false);
  const [deleteProgressInfoWarning, setDeleteProgressInfoWarning] =
    useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [toastrVariable, setToastrVariable] = useState<AlertColor>('info');
  const [toastrDefaultMessage, setToastrDefaultMessage] = useState('');
  const [toastrId, setToastrId] = useState('');
  const [editContext, setEditContext] = useState<{
    outcomeIndex: number | null;
    progressIndex: number | null;
    id?: string;
  }>({
    outcomeIndex: null,
    progressIndex: null,
  });

  const { toggleLoader } = useContext(LoaderContext) as LoaderContextType;
  const validationError = useOutcomeValidationCheck();
  const isEditMode = editContext.progressIndex !== null;

  const hasTruthyValue = progressInfo.some(
    ({ date, progressDescription, progressRating }) =>
      Boolean(date) || Boolean(progressDescription) || Boolean(progressRating),
  );

  useEffect(() => {
    dispatch(
      updateIfspUnsavedFields({
        ...ifspUnsavedFields,
        isOutComeProgressInfoFieldsChange: hasTruthyValue,
      }),
    );
  }, [dispatch, hasTruthyValue, ifspUnsavedFields]);

  useEffect(() => {
    setProgressInfo([
      {
        id: '',
        tempId: uuidv4(),
        date: '',
        progressDescription: '',
        progressRating: '',
        isSaveClicked: false,
      },
    ]);
  }, [isUnsavedPopupConfirmed]);

  useEffect(() => {
    setProgressInfo((prev) =>
      prev.map((item) =>
        item.isSaveClicked ? { ...item, isSaveClicked: false } : item,
      ),
    );
  }, [isMainOutComeSaveSuccessTrigger]);

  useEffect(() => {
    if (ifspOutComes.outcome.length > 0) {
      // Create an array with the same length as outcomes
      const progressArray: ProgressInfoType[] = ifspOutComes.outcome.map(
        () => ({
          id: '',
          date: '',
          progressDescription: '',
          progressRating: '',
          isSaveClicked: false,
        }),
      );

      setProgressInfo(progressArray);
    } else {
      setProgressInfo([
        {
          id: '',
          tempId: uuidv4(),
          date: '',
          progressDescription: '',
          progressRating: '',
          isSaveClicked: false,
        },
      ]);
    }
  }, [ifspOutComes.outcome.length]);

  const handleAddAdditionalOutcome = () => {
    setProgressInfo((prevProgressInfo) => {
      return [
        ...prevProgressInfo,
        {
          id: '',
          tempId: uuidv4(),
          date: '',
          progressDescription: '',
          progressRating: '',
          isSaveClicked: false,
        },
      ];
    });
    dispatch(
      updateIfspOutcomes({
        ...ifspOutComes,
        outcome: [
          ...ifspOutComes.outcome,
          {
            id: '',
            tempId: uuidv4(),
            concernPriority: '',
            outcome: '',
            measurableCriteriaProcedures: '',
            timeFrame: '',
            startDate: '',
            endDate: '',
            tbrOutcomeId: 0,
            tbrUpdateRequired: false,
            outcomeProgressList: [],
          },
        ],
      }),
    );
  };

  const handleSaveOrUpdateProgressInfo = () => {
    const current = progressInfo[openIndex];
    if (!current) return;

    const updatedOutcome = ifspOutComes.outcome.map((item, index) => {
      if (index !== openIndex) return item;

      const newEntry: OutcomeProgress = {
        id: editContext.id || '',
        tempId: editContext.id ? undefined : uuidv4(),
        date: current.date,
        progressDescription: current.progressDescription,
        progressRating: current.progressRating,
      };

      if (isEditMode) {
        const updatedList = [...item.outcomeProgressList];
        updatedList[editContext.progressIndex!] = newEntry;
        return { ...item, outcomeProgressList: updatedList };
      }

      return {
        ...item,
        outcomeProgressList: [...item.outcomeProgressList, newEntry],
      };
    });

    dispatch(updateIfspOutcomes({ ...ifspOutComes, outcome: updatedOutcome }));

    // ✅ reset form AFTER save
    setProgressInfo((prev) =>
      prev.map((item, i) =>
        i === openIndex
          ? {
              id: '',
              tempId: uuidv4(),
              date: '',
              progressDescription: '',
              progressRating: '',
              isSaveClicked: false,
            }
          : item,
      ),
    );

    setEditContext({ outcomeIndex: null, progressIndex: null });
  };

  const handleEditProgressInfo = (
    data: OutcomeProgress,
    progressIndex: number,
  ) => {
    setEditContext({
      outcomeIndex: openIndex,
      progressIndex,
      id: data.id,
    });

    setProgressInfo((prev) =>
      prev.map((item, i) =>
        i === openIndex
          ? {
              ...item,
              id: data.id,
              date: data.date,
              progressDescription: data.progressDescription,
              progressRating: data.progressRating,
              isSaveClicked: false,
            }
          : item,
      ),
    );
  };

  const handleDeleteProgressInfo = (
    data: OutcomeProgress,
    progressIndex: number,
  ) => {
    setEditContext({
      outcomeIndex: openIndex,
      progressIndex,
      id: data.id,
    });
    setDeleteProgressInfoWarning(true);
  };

  const handledeleteOutcomeProgressInfo = () => {
    const { outcomeIndex, progressIndex, id } = editContext;
    if (outcomeIndex === null || progressIndex === null) return;

    const updatedOutcome = ifspOutComes.outcome.map((item, index) => {
      if (index !== outcomeIndex) return item;

      return {
        ...item,
        outcomeProgressList: item.outcomeProgressList.filter(
          (_, i) => i !== progressIndex,
        ),
      };
    });

    toggleLoader(true);

    const finalize = () => {
      dispatch(
        updateIfspOutcomes({ ...ifspOutComes, outcome: updatedOutcome }),
      );
      setEditContext({ outcomeIndex: null, progressIndex: null });
      setDeleteProgressInfoWarning(false);
      toggleLoader(false);
    };

    if (id) {
      deleteOutcomeProgressInfo(id)
        .then(() => {
          setOpen(true);
          setToastrVariable('success');
          setToastrId('IFSP.OutcomeProgress.DeleteSuccess');
          setToastrDefaultMessage('Outcome progress deleted successfully');
        })
        .catch(() => {
          setOpen(true);
          setToastrVariable('error');
          setToastrId('IFSP.OutcomeProgress.DeleteError');
          setToastrDefaultMessage('Failed to delete outcome progress');
        })
        .finally(finalize);
    } else {
      finalize();
    }
  };

  const handleSubsectionsDeleteData = (id: string, index: number) => {
    setDeleteOutComeIdAndIndex({ id, index });
    setDeleteOutcomeWarning(true);
  };

  const handledeleteOutcome = () => {
    toggleLoader(true);
    const updatedOutCome = ifspOutComes.outcome.filter(
      (_, i) => i !== deleteOutComeIdAndIndex.index,
    );
    if (deleteOutComeIdAndIndex.id) {
      deleteIfspOutcome(deleteOutComeIdAndIndex.id)
        .then(() => {
          setOpen(true);
          setToastrVariable('success');
          setToastrId('IFSP.Outcome.DeleteSuccess');
          setToastrDefaultMessage('Outcome deleted successfully');
          dispatch(
            updateIfspOutcomes({
              outcome: updatedOutCome,
            }),
          );
          dispatch(
            updateOriginalOutcomes({
              outcome: updatedOutCome,
            }),
          );
        })
        .catch(() => {
          setOpen(true);
          setToastrVariable('error');
          setToastrId('IFSP.Outcome.DeleteError');
          setToastrDefaultMessage('Failed to delete outcome');
        })
        .finally(() => {
          toggleLoader(false);
          setDeleteOutcomeWarning(false);
        });
    } else {
      dispatch(
        updateIfspOutcomes({
          outcome: updatedOutCome,
        }),
      );
      toggleLoader(false);
      setDeleteOutcomeWarning(false);
    }
  };

  const handleCloseSnakBar = (
    _event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  if (!ifspOutComes.ifspId) return null;

  return (
    <Box component="main">
      <InformationModal
        open={openInfoModal}
        titleLabel="IFSP.Information"
        titleDefaultLabel="Information"
        buttonLabel=" IFSP.InfoOkButton"
        buttonDefaultLabel="Ok"
        onClose={() => {
          setOpenInfoModal(false);
        }}
        infoLabelId={'IFSP.ChildAndFamilyOutcomesInfo'}
        infoDefaultLabel={info}
      />
      <ModalPopup
        open={deleteOutcomeWarning}
        description="permanentDeleteConfirmationMessage"
        onOk={() => {
          handledeleteOutcome();
        }}
        onCancel={() => {
          setDeleteOutcomeWarning(false);
        }}
        labelId1="Clientpage.cancelbtn"
        negativeActionLabel="cancelText"
        labelId2="Clientpage.Okbtn"
        positiveActionLabel="Ok"
      />
      <ModalPopup
        open={deleteProgressInfoWarning}
        description="permanentDeleteConfirmationMessage"
        onOk={() => {
          handledeleteOutcomeProgressInfo();
        }}
        onCancel={() => {
          setDeleteProgressInfoWarning(false);
        }}
        labelId1="Clientpage.cancelbtn"
        negativeActionLabel="cancelText"
        labelId2="Clientpage.Okbtn"
        positiveActionLabel="Ok"
      />
      <SnackBarComponent
        open={open}
        handleClose={handleCloseSnakBar}
        successOrError={toastrVariable}
        labelId={toastrId}
        defaultMessageId={toastrDefaultMessage}
      />
      <InformationModal
        open={openReviewInfoModal}
        titleLabel="IFSP.Information"
        titleDefaultLabel="Information"
        buttonLabel=" IFSP.InfoOkButton"
        buttonDefaultLabel="Ok"
        onClose={() => {
          setOpenReviewInfoModal(false);
        }}
        infoLabelId={'IFSP.OutcomeProgressReviewInfo'}
        infoDefaultLabel={reviewInfo}
      />
      <Box
        component="section"
        className="p-lg mt-md borderRadius4"
        sx={{
          border:
            ifspMainPageDetails.outcomeTbrUpdateRequired &&
            !ifspMainPageDetails.isOutcomeChecked
              ? '1px solid #F96300'
              : '1px solid #00C6B8',
          backgroundColor: '#ECF9F8',
        }}
      >
        <Box component="div" className="flex__justify__space-between">
          <Box component="div" className="flex__justify__space-between">
            <MediumTypography
              labelid="IFSP.session3"
              defaultlabel="Child And Family IFSP Outcomes"
              fontSize="18px"
              fontweight={600}
              textColor="#2A4241"
              className="pr-md"
            />
            <InfoIcon
              className="cursorPointer"
              onClick={() => {
                setOpenInfoModal(true);
              }}
            />
          </Box>
        </Box>
        <Box component="div">
          {ifspOutComes?.outcome?.map((maindataForRef, subSectionIndex) => (
            <Box
              component="div"
              key={maindataForRef.id || maindataForRef.tempId}
            >
              <Box
                component="div"
                className="mt-lg mb-lg"
                sx={{
                  borderTop:
                    subSectionIndex === 0 ? '1px solid #00C6B8' : 'none',
                  paddingTop: subSectionIndex === 0 ? '24px' : '0px',
                  paddingBottom:
                    showSubSection && openIndex === subSectionIndex
                      ? '0px'
                      : '24px',
                  borderBottom:
                    showSubSection && openIndex === subSectionIndex
                      ? 'none'
                      : '1px solid #00C6B8',
                }}
              >
                <Box className="flex__justify__space-between">
                  <Box component="div" className="flex__ align__items__center">
                    <MediumTypography
                      labelid="IFSP.outComes"
                      defaultlabel="Outcomes"
                      textColor="#2A4241"
                      fontSize="16px"
                      fontweight={600}
                      className="mr-xs"
                    />
                    <InfoIcon
                      className="cursorPointer"
                      onClick={() => {
                        setOpenReviewInfoModal(true);
                      }}
                    />
                    {(!showSubSection || openIndex !== subSectionIndex) &&
                      maindataForRef.startDate && (
                        <Box className="flex__ align__items__center">
                          <MediumTypography
                            label={dayjs(maindataForRef.startDate).format(
                              'MM/DD/YYYY',
                            )}
                            textColor="var(--thom-green-primary)"
                            fontSize="14px"
                            fontweight={600}
                            className="ml-md"
                          />

                          <MediumTypography
                            labelid="r"
                            defaultlabel="to"
                            textColor="var(--thom-green-primary)"
                            fontSize="14px"
                            fontweight={600}
                            className="mr-sm ml-sm"
                          />

                          <MediumTypography
                            label={
                              maindataForRef.endDate
                                ? dayjs(maindataForRef.endDate).format(
                                    'MM/DD/YYYY',
                                  )
                                : '-'
                            }
                            textColor="var(--thom-green-primary)"
                            fontSize="14px"
                            fontweight={600}
                          />
                        </Box>
                      )}
                  </Box>
                  <Box component="div" className=" flex__ align__items__center">
                    {(!showSubSection || openIndex !== subSectionIndex) &&
                    isSaveClicked &&
                    (validationError.outcome.some(
                      (item) =>
                        item.errorIndex === subSectionIndex && item.error,
                    ) ||
                      validationError.startDate.some(
                        (item) =>
                          item.errorIndex === subSectionIndex && item.error,
                      ) ||
                      validationError.endDate.some(
                        (item) =>
                          item.errorIndex === subSectionIndex && item.error,
                      )) ? (
                      <MediumTypography
                        labelid="IFSP.ChildOutcomeUpdate.Error"
                        defaultlabel="Please correct the highlighted fields before submitting."
                        className="errorText-md"
                      />
                    ) : (
                      (!showSubSection || openIndex !== subSectionIndex) &&
                      ifspOutComes?.outcome[subSectionIndex]?.id &&
                      ifspOutComes?.outcome[subSectionIndex]
                        ?.tbrUpdateRequired === true &&
                      !ifspMainPageDetails.isOutcomeChecked && (
                        <MediumTypography
                          labelid="IFSP.ChildOutcomeTBRSync.Warning"
                          defaultlabel="Data is not fully synchronized with TBR"
                          textColor="#F96300"
                        />
                      )
                    )}

                    {((ifspOutComes.outcome.length > 1 &&
                      ifspPermission.hasClientEditPermission &&
                      ifspOutComes.outcome[subSectionIndex].tbrOutcomeId < 1) ||
                      (ifspPermission.hasIfspDeletePermissionAfterLock &&
                        ifspPermission.hasClientEditPermission &&
                        ifspOutComes.outcome.length > 1)) && (
                      <Box
                        component="div"
                        className="cursorPointer flex__ align__items__center ml-lg"
                        onClick={() => {
                          handleSubsectionsDeleteData(
                            maindataForRef.id,
                            subSectionIndex,
                          );
                        }}
                      >
                        <Delete />
                        <MediumTypography
                          labelid="IFSP.Delete"
                          defaultlabel="Delete"
                          textColor="#EB4C60"
                          fontweight={700}
                          fontSize="16px"
                          className="ml-xs"
                        />
                      </Box>
                    )}

                    {showSubSection && openIndex === subSectionIndex ? (
                      <UpArrow
                        onClick={() => {
                          setShowSubSection(false);
                          setOpenIndex(subSectionIndex);
                        }}
                        className="cursorPointer ml-lg"
                      />
                    ) : (
                      <DownArrow
                        onClick={() => {
                          setShowSubSection(true);
                          setOpenIndex(subSectionIndex);
                        }}
                        className="cursorPointer ml-lg"
                      />
                    )}
                  </Box>
                </Box>
                {showSubSection && openIndex === subSectionIndex ? null : (
                  <Tooltip
                    placement="bottom"
                    title={maindataForRef.outcome ?? '-'}
                    arrow={true}
                    followCursor
                  >
                    <Box
                      sx={{
                        width: 'fit-content',
                        maxWidth: '70%',
                      }}
                    >
                      <MediumTypography
                        label={maindataForRef.outcome || '-'}
                        textColor="#2A4241"
                        fontSize="14px"
                        textstyle="justify"
                        fontweight={500}
                        sxProps={{
                          width: 'fit-content',
                          maxWidth: '100%',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          wordBreak: 'break-word',
                        }}
                        className="mt-sm"
                      />
                    </Box>
                  </Tooltip>
                )}
              </Box>

              {showSubSection && openIndex === subSectionIndex && (
                <Box
                  component="div"
                  className="pb-lg"
                  sx={{
                    borderBottom: '1px solid #00C6B8',
                  }}
                >
                  <Box component="div">
                    <Grid container rowSpacing={'16px'}>
                      <Grid item xs={5}>
                        <MediumTypography
                          labelid={'IFSP.consernPriority'}
                          defaultlabel={'Concern Priority:'}
                          className="mb-xs"
                        />
                        <CommentTextbox
                          disabled={!ifspPermission.hasClientEditPermission}
                          placeholder="Enter comments here"
                          name="concernPriority"
                          Value={
                            ifspOutComes.outcome[subSectionIndex]
                              .concernPriority
                          }
                          maxLength={500}
                          handlechange={(text) => {
                            dispatch(
                              updateIfspOutcomes({
                                ...ifspOutComes,
                                outcome: ifspOutComes.outcome.map(
                                  (item, index) =>
                                    index === subSectionIndex
                                      ? {
                                          ...item,
                                          concernPriority: text || null,
                                        }
                                      : item,
                                ),
                              }),
                            );
                          }}
                          rows={3}
                          placeholderBackgroundColor="#fff"
                        />
                      </Grid>
                      <Grid item xs={0.5}></Grid>

                      <Grid item xs={5}>
                        <MediumTypography
                          labelid={'IFSP.outcomeChildFamily'}
                          defaultlabel={
                            'What changes do we want to see happen for our child and family?'
                          }
                          className="mb-xs"
                        />
                        <CommentTextbox
                          disabled={
                            (ifspOutComes.outcome[subSectionIndex]
                              .tbrOutcomeId > 0 &&
                              !ifspPermission.hasIfspEditPermissionAfterLock) ||
                            !ifspPermission.hasClientEditPermission
                          }
                          placeholder="Enter comments here"
                          name="What changes do we want to see happen for our child and family?"
                          Value={ifspOutComes.outcome[subSectionIndex].outcome}
                          maxLength={500}
                          handlechange={(text) => {
                            dispatch(
                              updateIfspOutcomes({
                                ...ifspOutComes,
                                outcome: ifspOutComes.outcome.map(
                                  (item, index) =>
                                    index === subSectionIndex
                                      ? {
                                          ...item,
                                          outcome: text || null,
                                        }
                                      : item,
                                ),
                              }),
                            );
                          }}
                          rows={3}
                          Required
                          placeholderBackgroundColor="#fff"
                        />
                        {isSaveClicked &&
                          validationError.outcome
                            .filter(
                              (item) => item.errorIndex === subSectionIndex,
                            )
                            .map((item) => (
                              <MediumTypography
                                key={item.errorMessageId}
                                className="errorText-md"
                                labelid={item.errorMessageId}
                                defaultlabel={item.errorMessage}
                              />
                            ))}
                      </Grid>
                      <Grid item xs={5}>
                        <MediumTypography
                          labelid={'IFSP.outComeMeasurable'}
                          defaultlabel={
                            'How will we measure and know when we have met this outcome?'
                          }
                          className="mb-xs"
                        />
                        <CommentTextbox
                          disabled={!ifspPermission.hasClientEditPermission}
                          placeholder="Enter comments here"
                          name="measurableProcedure"
                          Value={
                            ifspOutComes.outcome[subSectionIndex]
                              .measurableCriteriaProcedures
                          }
                          maxLength={500}
                          handlechange={(text) => {
                            dispatch(
                              updateIfspOutcomes({
                                ...ifspOutComes,
                                outcome: ifspOutComes.outcome.map(
                                  (item, index) =>
                                    index === subSectionIndex
                                      ? {
                                          ...item,
                                          measurableCriteriaProcedures:
                                            text || null,
                                        }
                                      : item,
                                ),
                              }),
                            );
                          }}
                          rows={3}
                          placeholderBackgroundColor="#fff"
                        />
                      </Grid>
                      <Grid item xs={0.5}></Grid>

                      <Grid item xs={5}>
                        <MediumTypography
                          labelid={'IFSP.outComeDesired'}
                          defaultlabel={
                            'When do we think we might achieve the desired outcome?'
                          }
                          className="mb-xs"
                        />
                        <CommentTextbox
                          disabled={!ifspPermission.hasClientEditPermission}
                          placeholder="Enter comments here"
                          name="targetDate"
                          Value={
                            ifspOutComes.outcome[subSectionIndex].timeFrame
                          }
                          maxLength={500}
                          handlechange={(text) => {
                            dispatch(
                              updateIfspOutcomes({
                                ...ifspOutComes,
                                outcome: ifspOutComes.outcome.map(
                                  (item, index) =>
                                    index === subSectionIndex
                                      ? {
                                          ...item,
                                          timeFrame: text || null,
                                        }
                                      : item,
                                ),
                              }),
                            );
                          }}
                          rows={3}
                          placeholderBackgroundColor="#fff"
                        />
                      </Grid>
                      <Grid item xs={5}>
                        <DatePickerComponent
                          key={ifspOutComes.outcome[subSectionIndex].startDate}
                          disabledDate={
                            (ifspOutComes.outcome[subSectionIndex]
                              .tbrOutcomeId > 0 &&
                              !ifspPermission.hasIfspEditPermissionAfterLock) ||
                            !ifspPermission.hasClientEditPermission
                          }
                          className="bg__white"
                          required={true}
                          labelid={'IFSP.startDate'}
                          defaultlabelid={'Start Date*'}
                          disableFuture={false}
                          value={
                            ifspOutComes.outcome[subSectionIndex].startDate
                              ? dayjs(
                                  ifspOutComes.outcome[subSectionIndex]
                                    .startDate,
                                )
                              : null
                          }
                          handlechange={(dateValue: Dayjs | null) => {
                            if (dateValue) {
                              const formattedDate =
                                dayjs(dateValue).format('MM/DD/YYYY');
                              dispatch(
                                updateIfspOutcomes({
                                  ...ifspOutComes,
                                  outcome: ifspOutComes.outcome.map(
                                    (item, index) =>
                                      index === subSectionIndex
                                        ? {
                                            ...item,
                                            startDate: formattedDate,
                                          }
                                        : item,
                                  ),
                                }),
                              );
                            } else {
                              dispatch(
                                updateIfspOutcomes({
                                  ...ifspOutComes,
                                  outcome: ifspOutComes.outcome.map(
                                    (item, index) =>
                                      index === subSectionIndex
                                        ? {
                                            ...item,
                                            startDate: null,
                                          }
                                        : item,
                                  ),
                                }),
                              );
                            }
                          }}
                        />
                        {isSaveClicked &&
                          validationError.startDate
                            .filter(
                              (item) => item.errorIndex === subSectionIndex,
                            )
                            .map((item) => (
                              <MediumTypography
                                key={item.errorMessageId}
                                className="errorText-md"
                                labelid={item.errorMessageId}
                                defaultlabel={item.errorMessage}
                              />
                            ))}
                      </Grid>
                      <Grid item xs={0.5}></Grid>
                      <Grid item xs={5}>
                        <DatePickerComponent
                          key={ifspOutComes.outcome[subSectionIndex].endDate}
                          disabledDate={!ifspPermission.hasClientEditPermission}
                          className="bg__white"
                          labelid={'IFSP.endDate'}
                          defaultlabelid={'End Date'}
                          disableFuture={false}
                          value={
                            ifspOutComes.outcome[subSectionIndex].endDate
                              ? dayjs(
                                  ifspOutComes.outcome[subSectionIndex].endDate,
                                )
                              : null
                          }
                          handlechange={(dateValue: Dayjs | null) => {
                            if (dateValue) {
                              const formattedDate =
                                dayjs(dateValue).format('MM/DD/YYYY');

                              dispatch(
                                updateIfspOutcomes({
                                  ...ifspOutComes,
                                  outcome: ifspOutComes.outcome.map(
                                    (item, index) =>
                                      index === subSectionIndex
                                        ? {
                                            ...item,
                                            endDate: formattedDate,
                                          }
                                        : item,
                                  ),
                                }),
                              );
                            } else {
                              dispatch(
                                updateIfspOutcomes({
                                  ...ifspOutComes,
                                  outcome: ifspOutComes.outcome.map(
                                    (item, index) =>
                                      index === subSectionIndex
                                        ? {
                                            ...item,
                                            endDate: null,
                                          }
                                        : item,
                                  ),
                                }),
                              );
                            }
                          }}
                        />
                        {isSaveClicked &&
                          validationError.endDate
                            .filter(
                              (item) => item.errorIndex === subSectionIndex,
                            )
                            .map((item) => (
                              <MediumTypography
                                key={item.errorMessageId}
                                className="errorText-md"
                                labelid={item.errorMessageId}
                                defaultlabel={item.errorMessage}
                              />
                            ))}
                      </Grid>
                    </Grid>
                  </Box>
                  <MediumTypography
                    labelid="IFSP.outcomesProgressInformation"
                    defaultlabel="Outcomes Progress Information"
                    textColor="#2A4241"
                    fontSize="16px"
                    fontweight={600}
                    className="mt-xlg pt-lg"
                    sxProps={{ borderTop: '1px solid #00C6B8' }}
                  />
                  <Grid container rowSpacing={'16px'} className="mt-xs">
                    <Grid item xs={5}>
                      <DatePickerComponent
                        key={progressInfo[subSectionIndex]?.date}
                        disabledDate={!ifspPermission.hasClientEditPermission}
                        labelid="IFSP.Date"
                        defaultlabelid="Date*"
                        required={true}
                        value={
                          progressInfo[subSectionIndex]?.date
                            ? dayjs(progressInfo[subSectionIndex]?.date)
                            : null
                        }
                        handlechange={(dateValue: Dayjs | null) => {
                          if (dateValue) {
                            const formattedDate =
                              dayjs(dateValue).format('MM/DD/YYYY');
                            setProgressInfo((prevData) => {
                              return prevData.map((item, index) => {
                                if (index === subSectionIndex) {
                                  return {
                                    ...item,
                                    date: formattedDate,
                                  };
                                }
                                return item;
                              });
                            });
                          } else {
                            setProgressInfo((prevData) => {
                              return prevData.map((item, index) => {
                                if (index === subSectionIndex) {
                                  return {
                                    ...item,
                                    date: '',
                                  };
                                }
                                return item;
                              });
                            });
                          }
                        }}
                        className="bg__white"
                      />
                      {!progressInfo[subSectionIndex]?.date &&
                        progressInfo[subSectionIndex]?.isSaveClicked && (
                          <MediumTypography
                            labelid="IFSP.OutComeProgress.dateRequired"
                            defaultlabel="Date is required"
                            className="errorText-md"
                          />
                        )}
                      {progressInfo[subSectionIndex]?.date &&
                        progressInfo[subSectionIndex]?.isSaveClicked &&
                        datePickerMinDateMaxDateValidate(
                          progressInfo[subSectionIndex]?.date,
                        ) && (
                          <MediumTypography
                            labelid="datePickerMinDateMaxDateValidate"
                            defaultlabel="Date is invalid"
                            className="errorText-md"
                          />
                        )}
                    </Grid>
                    <Grid item xs={0.5}></Grid>
                    <Grid item xs={5}></Grid>
                    <Grid item xs={5}>
                      <CommentTextbox
                        disabled={!ifspPermission.hasClientEditPermission}
                        placeholder="Progress Description"
                        name="IFSP.progressDesc"
                        Value={
                          progressInfo[subSectionIndex]?.progressDescription
                        }
                        maxLength={500}
                        handlechange={(text) => {
                          setProgressInfo((prevData) => {
                            return prevData.map((item, index) => {
                              if (index === subSectionIndex) {
                                return {
                                  ...item,
                                  progressDescription: text,
                                };
                              }
                              return item;
                            });
                          });
                        }}
                        rows={4}
                        Required
                        placeholderBackgroundColor="#fff"
                      />
                      {!progressInfo[subSectionIndex]?.progressDescription &&
                        progressInfo[subSectionIndex]?.isSaveClicked && (
                          <MediumTypography
                            labelid="IFSP.OutComeProgress.progressDescriptionRequired"
                            defaultlabel="Progress Description is required"
                            className="errorText-md"
                          />
                        )}
                    </Grid>
                    <Grid item xs={5}></Grid>
                    <Grid item xs={5}>
                      <DropdownComponent
                        disabled={!ifspPermission.hasClientEditPermission}
                        names={progressRatingsList}
                        defaultlabelid="Progress Rating"
                        labelid="IFSP.progressRating"
                        value={progressInfo[subSectionIndex]?.progressRating}
                        handleChange={(value: string) => {
                          setProgressInfo((prevData) => {
                            return prevData.map((item, index) => {
                              if (index === subSectionIndex) {
                                return {
                                  ...item,
                                  progressRating: value,
                                };
                              }
                              return item;
                            });
                          });
                        }}
                        Required
                      />
                      {!progressInfo[subSectionIndex]?.progressRating &&
                        progressInfo[subSectionIndex]?.isSaveClicked && (
                          <MediumTypography
                            labelid="IFSP.OutComeProgress.progressRatingRequired"
                            defaultlabel="Progress Rating is required"
                            className="errorText-md"
                          />
                        )}
                    </Grid>
                    <Grid item xs={0.5}></Grid>
                    {ifspPermission.hasClientEditPermission && (
                      <Grid item xs={5}>
                        <Box
                          className="cursorPointer flex__ align__items__center pt-xs"
                          onClick={() => {
                            setProgressInfo((prevData) => {
                              return prevData.map((item, index) => {
                                if (index === subSectionIndex) {
                                  return {
                                    ...item,
                                    isSaveClicked: true,
                                  };
                                }
                                return item;
                              });
                            });
                            if (
                              progressInfo[subSectionIndex]?.date &&
                              progressInfo[subSectionIndex]
                                ?.progressDescription &&
                              progressInfo[subSectionIndex]?.progressRating
                            ) {
                              handleSaveOrUpdateProgressInfo();
                            }
                          }}
                        >
                          <Box className="flex__ align__items__center pr-sm">
                            <AddIcon className="cursorPointer" />
                          </Box>
                          <MediumTypography
                            labelid={'IFSP.SaveProgressInformation'}
                            defaultlabel={'Save Progress Information'}
                            fontweight={400}
                            textColor="#2A4241"
                          />
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                  <Box component="div" className="respoScrollTable mt-md">
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHeader
                          headerNames={outComesProgressInfoheadCells}
                          checkBoxRequired={false}
                          className="listDataTableHead"
                        />
                        <TableBody className="tableRowcss ">
                          {openIndex === subSectionIndex &&
                            ifspOutComes.outcome[
                              subSectionIndex
                            ].outcomeProgressList.map(
                              (listData: OutcomeProgress, index) => (
                                <OutcomeProgressReviewInfoTableRow
                                  key={listData.id || listData.tempId}
                                  data={listData}
                                  onEdit={(data: OutcomeProgress) => {
                                    handleEditProgressInfo(data, index);
                                  }}
                                  onDelete={(data: OutcomeProgress) => {
                                    handleDeleteProgressInfo(data, index);
                                  }}
                                />
                              ),
                            )}
                        </TableBody>
                      </Table>
                      {ifspOutComes.outcome[subSectionIndex].outcomeProgressList
                        .length === 0 && (
                        <Box component="div" className="width__100">
                          <MediumTypography
                            textstyle={'center'}
                            labelid="IFSP.emptyMessage"
                            defaultlabel="No data added yet"
                            className="p-sm"
                          />
                        </Box>
                      )}
                    </TableContainer>
                  </Box>
                </Box>
              )}
            </Box>
          ))}
          <Box component="div" className="mt-md">
            <ButtonComponent
              disabled={!ifspPermission.hasClientEditPermission}
              className="btn-primary btn-submit"
              variantType="contained"
              type="submit"
              labelId="Add Additional Outcome"
              defaultLabelId="Add Additional Outcome"
              onClick={() => handleAddAdditionalOutcome()}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ChildAndFamilyOutcomes;
